(ns app.front.ui.viewport-observer
  "Reusable viewport observer that tracks how much of a DOM element is visible
  in the viewport. Stores measurements in re-frame app-db, accessible via subscriptions.

  Usage:
    ;; Mount the observer once at layout level (renders nothing visible):
    [viewport-observer/observer {:id :sticky-header :element-id \"root-sticky-header\"}]

    ;; Subscribe from any component:
    @(rf/subscribe [::viewport-observer/visible-bottom :sticky-header])"
  (:require [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

(rf/reg-event-db ::update-measurement
  (fn [db [_ id measurement]]
    (assoc-in db [::measurements id] measurement)))

(rf/reg-event-db ::remove-measurement
  (fn [db [_ id]]
    (update db ::measurements dissoc id)))

(rf/reg-event-db ::update-viewport-height
  (fn [db [_ height]]
    (assoc db ::viewport-height height)))

(rf/reg-sub ::measurement
  (fn [db [_ id]]
    (get-in db [::measurements id])))

(rf/reg-sub ::visible-bottom
  (fn [[_ id]]
    (rf/subscribe [::measurement id]))
  (fn [measurement _]
    (:visible-bottom measurement 0)))

(rf/reg-sub ::horizontal-overflow?
  (fn [[_ id]]
    (rf/subscribe [::measurement id]))
  (fn [{:keys [scroll-width client-width]
        :or   {scroll-width 0 client-width 0}}]
    (> scroll-width client-width)))

(rf/reg-sub ::viewport-height
  (fn [db _]
    (or (::viewport-height db) (.-innerHeight js/window))))

(defn- measure-element
  "Measure how much of an element is visible in the viewport.
  Returns its viewport position and horizontal dimensions."
  [element]
  (when element
    (let [rect (.getBoundingClientRect element)]
      {:visible-bottom (max 0 (.-bottom rect))
       :scroll-width   (.-scrollWidth element)
       :client-width   (.-clientWidth element)})))

(defn observer
  "Mounts an observer for a DOM element. Tracks element resize and optionally viewport
   scroll and resize to keep measurements current. Renders no visible output; consumers
   subscribe to ::measurement or a derived subscription such as ::visible-bottom.

   Props:
     :id         - keyword identifier for this measurement in app-db
     :element-id - DOM element ID string to observe"
  [{:keys [id element-id]}]
  (when ^boolean goog.DEBUG
    (when (nil? id)
      (.warn js/console "viewport-observer without id encountered")))
  (let [*raf-id          (atom nil)
        *element         (atom nil)
        *last-value      (atom nil)
        *last-vh         (atom nil)
        *resize-observer (atom nil)
        update!          (fn []
                           (when-not @*raf-id
                             (reset! *raf-id
                                     (.requestAnimationFrame
                                       js/window
                                       (fn []
                                         (reset! *raf-id nil)
                                         (let [vh (.-innerHeight js/window)]
                                           (when (not= vh @*last-vh)
                                             (reset! *last-vh vh)
                                             (rf/dispatch-sync [::update-viewport-height vh])))
                                         (when-let [el @*element]
                                           (let [measurement (measure-element el)]
                                             (when (not= measurement @*last-value)
                                               (reset! *last-value measurement)
                                               (rf/dispatch-sync [::update-measurement id measurement])))))))))]
    (r/create-class
      {:component-did-mount
       (fn []
         (let [el (.getElementById js/document element-id)]
           (reset! *element el)
           (when el
             (let [observer (js/ResizeObserver. (fn [_] (update!)))]
               (.observe observer el)
               (reset! *resize-observer observer))))
         (.addEventListener js/window "scroll" update! #js {:passive true})
         (.addEventListener js/window "resize" update!)
         (update!))
       :component-will-unmount
       (fn []
         (.removeEventListener js/window "scroll" update!)
         (.removeEventListener js/window "resize" update!)
         (some-> @*resize-observer .disconnect)
         (when-let [raf-id @*raf-id]
           (.cancelAnimationFrame js/window raf-id))
         (rf/dispatch [::remove-measurement id]))
       :reagent-render
       (fn [_] nil)})))
